import {
  HStack, Stack, Tag, Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { When } from 'react-if';
import { useGate } from 'statsig-react';

import { DeleteStudyAlert } from './components/DeleteStudyAlert';
import { SubscriptionGate } from './components/SubscriptionGate';
import {
  ClinicType,
  IAlertMessage,
  IStudieModel,
  StudyListResponse,
} from 'api/Models';
import { AsyncWrapper } from 'components/AsyncWrapper';
import ImagesPreview from 'components/table/ImagesPreview';
import SkylineTable from 'components/table/SkylineTable';
import useAxios from 'hooks/useAxios';
import { useModal } from 'hooks/useModal';
import { usePagedData } from 'hooks/usePagedData';
import useUserContext from 'hooks/useUserContext';
import { StatsigGates } from 'StatsigGates';
import { getDate } from 'utils/formatter';
import { getPatientName } from 'utils/name';

const dateTimeFormat = 'MMMM d, yyyy, hh:mm a';

function Studylist(): JSX.Element {
  const { user } = useUserContext();
  const isSubscriptionGate = useGate(StatsigGates.SUBSCRIPTION_GATE).value;
  const [subscriptionRequest] = useAxios<IAlertMessage>('/api/billing/subscription/check');
  const showSubscriptionGate = useMemo(() => {
    if (!isSubscriptionGate) return false;
    if (!subscriptionRequest.data) return false;
    return true;
  }, [isSubscriptionGate, subscriptionRequest.data]);

  const isHumanClinic = user?.clinic?.clinicType === ClinicType.Human;
  const {
    request, PaginationControl, refetchRequest,
  } = usePagedData<StudyListResponse>({
    path: '/api/study/studylist',
  });

  const [
    {
      isOpen: isDeleteStudyAlertOpen,
      data: deleteStudyData,
    },
    {
      handleDelete: handleDeleteStudy,
      onClose: onCloseDeleteStudyAlert,
    },
  ] = useModal<IStudieModel>({
    onClose: () => refetchRequest(),
  });

  const columnHelper = createColumnHelper<StudyListResponse>();

  const openOhifViewer = (row: StudyListResponse): void => {
    const { id } = row;
    const url = `/viewer/skyline/dicomjson?url=/api/ohif/${id}`;
    window.location.href = url;
  };

  const columns = useMemo(() => [
    columnHelper.accessor('patientName', {
      header: () => 'Name',
      cell: ({ row }) => (() => (
        <Stack spacing={1}>
          <Text fontWeight='bold' fontSize='md'>
            {getPatientName(
              row.original.patientName,
              row.original.responsiblePerson,
            )}
          </Text>
          <When condition={row.original.responsiblePerson}>
            <HStack spacing={3}>
              <Text fontSize='sm'>{row.original.responsiblePerson}</Text>
              <Tag size='sm' backgroundColor='#4E5E75'>
                {row.original.responsiblePersonRole}
              </Tag>
            </HStack>
          </When>
        </Stack>
      ))(),
    }),
    columnHelper.accessor((row) => row.patientId ?? 'Unknown', {
      id: 'patientId',
      header: () => 'Patient ID',
      cell: (info) => info.getValue().toString(),
    }),
    columnHelper.accessor((row) => getDate(row.studyDateTime || '', dateTimeFormat), {
      id: 'studyDateTime',
      header: () => 'Study Date',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((row) => row.previewImages?.[0]?.modality ?? '', {
      id: 'modality',
      header: () => 'Modality',
      cell: (info) => info.getValue().toString(),
    }),
    columnHelper.accessor((row) => row.descriptions ?? '', {
      id: 'description',
      header: () => 'Description',
      cell: (info) => info.getValue().toString(),
    }),
    // columnHelper.accessor((row) => row.imageCount.toString(), {
    //   id: 'numImages',
    //   header: () => 'Images',
    //   cell: (info) => info.getValue(),
    // }),
    columnHelper.display({
      id: 'ImagesPreview',
      header: () => 'Preview',
      cell: ({ row }) => (() => (
        <ImagesPreview images={row.original.previewImages} imageCount={row.original.imageCount} />
      ))(),
    }),
    // Disabling delete until we can audit
    // columnHelper.display(
    //   {
    //     id: 'actions',
    //     cell: ({ row }) => (() => (
    //       <ActionMenu
    //         {
    //         ...[
    //           { label: 'Delete', action: () => handleDeleteStudy(row.original) },
    //         ]
    //         }
    //       />
    //     ))(),
    //   },
    // ),
  ], [columnHelper]);

  const invisibleColumns = useMemo(() => {
    if (isHumanClinic) return { responsiblePerson: false };
  }, [isHumanClinic]);
  return (
    <AsyncWrapper requests={[request!, subscriptionRequest]}>
      {showSubscriptionGate && (
        <SubscriptionGate alert={subscriptionRequest.data!} />
      )}
      {!showSubscriptionGate && (
        <>
          <SkylineTable<StudyListResponse>
            data={request?.data?.records || []}
            columns={columns}
            isSearchable
            invisibleColumns={invisibleColumns}
            PaginationControl={PaginationControl}
            emptyTableState={{ message: 'No studies found for this clinic' }}
            onRowClick={openOhifViewer}
          />
          <When condition={isDeleteStudyAlertOpen}>
            <DeleteStudyAlert
              isOpen={isDeleteStudyAlertOpen}
              onClose={onCloseDeleteStudyAlert}
              data={deleteStudyData!}
            />
          </When>
        </>
      )}
    </AsyncWrapper>
  );
}

export default Studylist;
